import type { Auth0Client, RedirectLoginResult } from "@auth0/auth0-spa-js";
import { Browser } from "@capacitor/browser";

import { AuthProvider } from "@/enums/auth-provider";
import type { AuthenticationClient } from "@/lib/auth/authentication-client";
import { logLogoutOpenUrl, logLogoutRequested } from "@/lib/breadcrumbs";
import { appUrl, HASURA_ENDPOINT } from "@/lib/utils/config";
import { Sentry } from "@/lib/utils/sentry";

const HASURA_JWT_CLAIMS = "https://hasura.io/jwt/claims";
const X_HASURA_DEFAULT_ROLE = "x-hasura-default-role";
const HASURA_USER_ROLE = "user";

export class Auth0AuthenticationClient implements AuthenticationClient {
  constructor(readonly auth0Client: Auth0Client) {}

  get activeProvider(): AuthProvider {
    return AuthProvider.Auth0;
  }

  get hasuraEndpoint(): string {
    return HASURA_ENDPOINT;
  }

  async isAuthenticated(): Promise<boolean> {
    return await this.auth0Client.isAuthenticated();
  }

  async getTokenSilently(): Promise<string> {
    return await this.auth0Client.getTokenSilently();
  }

  async getUserSub(): Promise<string> {
    const user = await this.auth0Client.getUser();

    return user?.sub ?? "";
  }

  async hasValidRole(): Promise<boolean> {
    const user = await this.auth0Client.getUser();

    if (!user) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- safe
    return user[HASURA_JWT_CLAIMS]?.[X_HASURA_DEFAULT_ROLE] === HASURA_USER_ROLE;
  }

  async logout(): Promise<void> {
    logLogoutRequested({ returnTo: appUrl });

    await this.auth0Client.logout({
      logoutParams: {
        returnTo: appUrl,
      },
      async openUrl(url: string) {
        logLogoutOpenUrl({ logoutUrl: url });
        await Browser.open({
          url,
          windowName: "_self",
        });
      },
    });
  }

  /** Clears local Auth0 session cache without triggering a redirect. */
  async logoutSilently(): Promise<void> {
    await this.auth0Client.logout({ openUrl: false });
  }

  async handleRedirectCallback<T>(url: string): Promise<RedirectLoginResult<T>> {
    return await this.auth0Client.handleRedirectCallback<T>(url);
  }

  async loginWithRedirect(returnTo: string, retry?: number): Promise<void> {
    await this.auth0Client.loginWithRedirect({
      async openUrl(url) {
        Sentry.addBreadcrumb(
          {
            category: "auth",
            message: `Opening login URL: ${url}`,
            level: "info",
          },
          {
            url,
          },
        );

        await Browser.open({
          url,
          windowName: "_self",
        });
      },
      appState: { returnTo, retry },
    });
  }
}
